/**
 * @file dropzone.component.ts
 * @description Drag-and-drop / click-to-browse upload region with two
 * layouts:
 *   - compact (default): horizontal strip used at the top of KB detail.
 *   - hero (`[compact]="false"`): centered tall block used as the empty
 *     state of KB detail.
 *
 * Visual spec: docs/design_handoff_references_redesign/README.md §3.
 */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-dropzone',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dropzone.component.html',
  styleUrls: ['./dropzone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropzoneComponent {
  /** When `true`, render the horizontal compact layout. */
  @Input() compact = true;

  /**
   * Accept attribute forwarded to the hidden file input. The default
   * mirrors the set kb-api accepts for References uploads (and the
   * `supportLine` copy below) — keep the three in sync.
   */
  @Input() accept = '.pdf,.doc,.docx,.txt';

  /** Disables drop and click while `true`. */
  @Input() disabled = false;

  /** Title shown in the hero layout. */
  @Input() title = 'Drop papers to start indexing';

  /** Subtitle shown in the hero layout. */
  @Input() subtitle = 'Each paper is indexed and ready to cite in about a minute. PDFs work best.';

  /** Compact-layout body line (the "Drop papers here, or browse" phrasing). */
  @Input() compactLine = 'Drop papers here, or browse';

  /** Supported-file footnote. */
  @Input() supportLine = 'PDF, DOC, DOCX, TXT · up to 10 MB each';

  /** Hero-variant primary CTA label. */
  @Input() ctaLabel = 'Browse files';

  /** Fired with the dropped/picked files. */
  @Output() readonly files = new EventEmitter<File[]>();

  @ViewChild('fileInput', { static: false })
  fileInputRef?: ElementRef<HTMLInputElement>;

  readonly isDragging = signal(false);

  @HostBinding('class.is-dragging') get hostDragging(): boolean {
    return this.isDragging();
  }

  @HostBinding('class.is-disabled') get hostDisabled(): boolean {
    return this.disabled;
  }

  @HostBinding('class.is-compact') get hostCompact(): boolean {
    return this.compact;
  }

  @HostBinding('class.is-hero') get hostHero(): boolean {
    return !this.compact;
  }

  openPicker(): void {
    if (this.disabled) return;
    this.fileInputRef?.nativeElement.click();
  }

  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const list = input.files ? Array.from(input.files) : [];
    this.emitFiles(list);
    input.value = '';
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const dt = event.dataTransfer;
    const dropped = dt?.files ? Array.from(dt.files) : [];
    this.emitFiles(dropped);
  }

  private emitFiles(list: File[]): void {
    if (list.length > 0) {
      this.files.emit(list);
    }
  }
}
